import { useState, type ReactElement } from 'react'
import { observer } from 'mobx-react-lite'
import { TemplatePickerBase } from 'app/components/templates/TemplatePickerBase'
import type { SmsTemplate } from 'app/generated/graphql'
import { writeFallbacks } from 'app/lib/workflows/messageFallbacks'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useSmsTemplateStore } from 'app/stores/StoreContext'
import { useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { TemplateEditorDialog } from './TemplateEditorDialog'

type SmsTemplatePickerProps = {
  trigger: ReactElement
  /** How a template is displayed/searched, e.g. resolved against the open
   *  contact in the inbox, fallbacks included. Defaults to the raw body. */
  renderTemplate?: (template: SmsTemplate) => string
  onSelect: (template: SmsTemplate) => void
  /** Current composer text; enables "Save current message" capture. */
  currentBody?: string
}

/** SMS binding of the shared template picker: store + resolved previews +
 *  save-current-message capture. Insert policy stays with the surface. */
export const SmsTemplatePicker = observer(function SmsTemplatePicker({
  trigger,
  renderTemplate,
  onSelect,
  currentBody,
}: SmsTemplatePickerProps) {
  const store = useSmsTemplateStore()
  const workspace = useWorkspace()
  const [savingCurrent, setSavingCurrent] = useState(false)

  const toPreview = renderTemplate ?? ((t: SmsTemplate) => writeFallbacks(t.body, {}))
  const templates = store.forWorkspace(workspace?.id)
  // searchText carries the raw tokened body so searching "firstName" still
  // matches even when the preview shows the resolved text.
  const items = templates.map((t) => ({ id: t.id, name: t.name, preview: toPreview(t), searchText: t.body }))

  return (
    <>
      <TemplatePickerBase
        trigger={trigger}
        items={items}
        onOpen={() => bg(store.load())}
        onSelectId={(id) => {
          const template = templates.find((t) => t.id === id)
          if (template) onSelect(template)
        }}
        manageHref="/settings/templates/sms"
        footerStart={
          currentBody?.trim() ? (
            <button
              type="button"
              className="text-[11px] text-primary-600 hover:underline"
              onClick={() => setSavingCurrent(true)}
            >
              Save current message
            </button>
          ) : undefined
        }
      />
      <TemplateEditorDialog
        open={savingCurrent}
        initialBody={currentBody}
        saving={store.saving}
        onSave={async (input) => {
          const result = await store.create(input)
          if (result.error) {
            notifyError(`Couldn't save the template`)
            logError(`SmsTemplatePicker: save failed`, result.error.message)
            return
          }
          notifySuccess(`Template saved`)
          setSavingCurrent(false)
        }}
        onCancel={() => setSavingCurrent(false)}
      />
    </>
  )
})
